import { Component } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ConfirmationService, MessageService, SharedModule } from 'primeng/api';
import { Button } from 'primeng/button';
import { IconField } from 'primeng/iconfield';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputIcon } from 'primeng/inputicon';
import { InputText } from 'primeng/inputtext';
import { TableModule } from 'primeng/table';
import { Tooltip } from 'primeng/tooltip';
import { debounceTime, Subject } from 'rxjs';
import SampleFirmwareInstallationStatus
    from '../../../../types/sample-firmware-revision/SampleFirmwareInstallationStatus';
import SampleFirmwareRevision from '../../../../types/sample-firmware-revision/SampleFirmwareRevision';
import SampleFirmwareRevisionWriteDTO from '../../../../types/sample-firmware-revision/SampleFirmwareRevisionWriteDTO';
import SampleHardwareRevision from '../../../../types/sample-hardware-revision/SampleHardwareRevision';
import Page from '../../../../types/shared/Page';
import { IsAdminDirective } from '../../directives/is-admin.directive';
import { SampleFirmwareRevisionService } from '../../services/sample-firmware-revision.service';
import { SampleHardwareRevisionService } from '../../services/sample-hardware-revision.service';
import { UtilsService } from '../../services/utils.service';
import { PageHeaderComponent } from '../shared/page-header/page-header.component';
import {
    CreateSampleFirmwareRevisionDialogComponent
} from '../shared/sample-firmware/create-sample-firmware-revision-dialog/create-sample-firmware-revision-dialog.component';
import {
    UpdateSampleFirmwareRevisionDialogComponent
} from '../shared/sample-firmware/update-sample-firmware-revision-dialog/update-sample-firmware-revision-dialog.component';

@Component({
    selector: 'app-sample-firmware',
    imports: [
        PageHeaderComponent,
        IconField,
        InputIcon,
        FormsModule,
        TableModule,
        Button,
        SharedModule,
        CreateSampleFirmwareRevisionDialogComponent,
        UpdateSampleFirmwareRevisionDialogComponent,
        InputText,
        IftaLabelModule,
        IsAdminDirective,
        Tooltip,
    ],
    templateUrl: './sample-firmware.component.html',
    styleUrl: './sample-firmware.component.scss'
})
export class SampleFirmwareComponent {
    public search = '';
    public sampleFirmwareRevisions: SampleFirmwareRevision[] = [];
    public totalRecords = 0;
    public rows = 10;
    public currentPage = 0;
    public loading = false;
    public displayCreateSampleFirmwareRevisionDialog = false;
    public displayUpdateSampleFirmwareRevisionDialog = false;
    public selectedSampleFirmwareRevision: SampleFirmwareRevisionWriteDTO = new SampleFirmwareRevisionWriteDTO({});
    public allInstallationStatuses: SampleFirmwareInstallationStatus[] = [];
    public allAvailableSampleHardwareRevisions: SampleHardwareRevision[] = [];

    private searchSubject = new Subject<string>();

    constructor(private sampleFirmwareRevisionService: SampleFirmwareRevisionService,
                private sampleHardwareRevisionService: SampleHardwareRevisionService,
                private messageService: MessageService,
                private confirmationService: ConfirmationService,
                private utilsService: UtilsService) {
    }

    /**
     * Initializes the component and loads the sample types.
     */
    public ngOnInit(): void {
        this.loadSampleFirmwareRevisions();
        this.searchSubject.pipe(debounceTime(300)).subscribe(() => {
            this.currentPage = 0;
            this.loadSampleFirmwareRevisions();
        });
        this.loadAllInstallationStatuses();
        this.loadCompatibleHardwares();
    }

    /**
     * Loads the sample firmware revisions from the server.
     */
    public loadSampleFirmwareRevisions(): void {
        this.loading = true;
        const size = this.rows;
        const search = this.search || '';

        this.sampleFirmwareRevisionService
            .getSampleFirmwareRevisions(search, this.currentPage, size)
            .then((result: Page<SampleFirmwareRevision>) => {
                this.sampleFirmwareRevisions = result.content;
                this.totalRecords = result.page.totalElements;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Loads all available hardware revisions.
     */
    public loadCompatibleHardwares() {
        this.sampleHardwareRevisionService.getAllSampleHardwareRevisions()
            .then((sampleHardwareRevisions: SampleHardwareRevision[]) => {
                this.allAvailableSampleHardwareRevisions = sampleHardwareRevisions;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Loads the sample installation status.
     */
    public loadAllInstallationStatuses() {
        this.sampleFirmwareRevisionService.getAllSampleFirmwareInstallationStatuses()
            .then((sampleFirmwareInstallationStatuses: SampleFirmwareInstallationStatus[]) => {
                this.allInstallationStatuses = sampleFirmwareInstallationStatuses;
            })
            .catch((error: any) => {
                this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
            })
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Loads all the installation statuses and maps the provided installation status.
     */
    public loadInstallationStatusDescription(installationStatus: string): string | null {
        const installationStatusFound = this.allInstallationStatuses.find(s => s.name === installationStatus);
        return installationStatusFound ? installationStatusFound.description : null;
    }

    /**
     * Loads compaitble sample hardware revisions.
     *
     * @param sampleFirmwareRevision The sampleFirmwareRevision.
     */
    public resolveHardwareRevisions(sampleFirmwareRevision: SampleFirmwareRevision): string {
        if (!sampleFirmwareRevision.hardwareRevisions || sampleFirmwareRevision.hardwareRevisions.length === 0) {
            return 'No data';
        }
        return sampleFirmwareRevision.hardwareRevisions.map(sampleHardwareRevision => sampleHardwareRevision.pcbVersion)
            .join(', ');
    }

    /**
     * Resolves if Sample Firmware is removable.
     *
     * @param sampleFirmwareRevision The sampleFirmwareRevision.
     */
    public resolveIfSampleFirmwareRemovable(sampleFirmwareRevision: SampleFirmwareRevision): boolean {
        const installationStatusFound = this.allInstallationStatuses.find(
            s => s.name === sampleFirmwareRevision.installationStatus);
        return installationStatusFound?.sequence !== 1;

    }

    /**
     * Handles the page change event.
     *
     * @param event The page change event.
     */
    public onPageChange(event: any): void {
        this.currentPage = Math.floor(event.first / event.rows);
        this.rows = event.rows;
        this.loadSampleFirmwareRevisions();
    }

    /**
     * Handles the search input change event.
     */
    public onSearchInputChange(): void {
        this.searchSubject.next(this.search);
    }

    /**
     * Opens the edit sample type dialog.
     */
    public editSampleFirmwareRevision(sampleFirmwareRevision: SampleFirmwareRevision): void {
        this.selectedSampleFirmwareRevision = new SampleFirmwareRevisionWriteDTO(sampleFirmwareRevision);
        this.displayUpdateSampleFirmwareRevisionDialog = true;
    }

    /**
     * Deletes a sample type after user confirmation.
     *
     * @param sampleFirmwareRevision The sample type to delete.
     */
    public deleteSampleFirmwareRevision(sampleFirmwareRevision: SampleFirmwareRevision): void {
        this.confirmationService.confirm({
            message: 'Are you sure you want to delete the sample firmware revision ' +
                sampleFirmwareRevision.releaseVersion + '?',
            header: 'Confirm Deletion',
            icon: 'pi pi-exclamation-triangle',
            accept: () => {
                this.sampleFirmwareRevisionService.deleteSampleFirmwareRevision(sampleFirmwareRevision.id).then(() => {
                    this.loadSampleFirmwareRevisions();
                    this.messageService.add(
                        {
                            severity: 'success',
                            summary: 'Success',
                            detail: 'Sample firmware revision deleted',
                            life: 3000
                        });
                }).catch((error) => {
                    this.messageService.add({ severity: 'error', summary: 'Error', detail: error.message, life: 3000 });
                });
            }
        });
    }

    /**
     * Opens the dialog to create a new sample type.
     */
    public openCreateSampleFirmwareRevisionDialog(): void {
        this.displayCreateSampleFirmwareRevisionDialog = true;
    }

    /**
     * Reloads the sample types.
     */
    public reloadSampleFirmwareRevisions(): void {
        this.loadSampleFirmwareRevisions();
    }

    /**
     * Download uploaded firmware revision file.
     */
    public async downloadFirmwareFile(id: number): Promise<void> {
        try {
            const sampleFirmwareRevisionFileDTO = await this.sampleFirmwareRevisionService.getSampleFirmwareRevisionFile(
                id);

            const computedHash = this.utilsService.sha512HexFromBase64(sampleFirmwareRevisionFileDTO.fileData);
            if (computedHash !== sampleFirmwareRevisionFileDTO.fileHash) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'File hash mismatch : corrupted file data',
                    life: 3000
                });
                return;
            }

            const blob = this.base64ToBlob(
                sampleFirmwareRevisionFileDTO.fileData, sampleFirmwareRevisionFileDTO.fileContentType);
            const filename = sampleFirmwareRevisionFileDTO.fileName.endsWith('.bin') ?
                sampleFirmwareRevisionFileDTO.fileName : `${ sampleFirmwareRevisionFileDTO.fileName }.bin`;

            // Create download link
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error downloading file:', error);
            this.messageService.add(
                { severity: 'error', summary: 'Error', detail: 'Error downloading file', life: 3000 });
        }
    }

    /**
     * Convertion of the file data to base64 before sending it to server.
     */
    public base64ToBlob(base64: string, contentType: string): Blob {
        const byteCharacters = atob(base64);
        const buffer = new ArrayBuffer(byteCharacters.length);
        const bytes = new Uint8Array(buffer);

        for (let i = 0; i < byteCharacters.length; i++) {
            bytes[i] = byteCharacters.charCodeAt(i);
        }

        return new Blob([ buffer ], { type: contentType });
    }
}
